import * as React from 'react';
import { ScrollArea as ScrollAreaPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

/**
 * Opt-in fix for Radix's viewport wrapper widening past the ScrollArea.
 *
 * Radix renders the viewport's children inside a
 * `<div style="min-width:100%; display:table">`. An auto-layout table is sized
 * by its contents' *min-content* width, and `white-space: nowrap` — what
 * Tailwind's `truncate` sets — makes a string's min-content width equal its
 * full rendered width. So a single long unbroken value grows the wrapper past
 * the viewport, `overflow-x: hidden` clips the overflow, and because this
 * component only ever renders a *vertical* `ScrollBar` there is no way to
 * scroll to what was cut off. Anything pinned to the right of that row (an
 * action button, an `absolute right-2` icon) simply disappears — and the
 * `truncate` never engages, because the row grew instead of the text shrinking.
 * `min-w-0` cannot help: it removes a flex item's automatic minimum size, but
 * does not reduce a box's min-content *contribution*.
 *
 * Forcing the wrapper to `display: block` makes it fill the viewport width
 * instead of its contents' width, so `min-w-0` + `truncate` behave normally.
 * `!important` is required because Radix sets `display` as an inline style.
 *
 * Opt in per call site rather than applying this globally — some ScrollAreas
 * deliberately overflow horizontally (`active-filter-chip-list.tsx` uses
 * `w-max` chips, `permission-matrix.tsx` a `min-w-full` table) and would be
 * squeezed by it.
 *
 * @example
 * <ScrollArea viewportProps={{ className: SCROLL_AREA_CONSTRAIN_WIDTH }}>
 */
export const SCROLL_AREA_CONSTRAIN_WIDTH = '[&>div]:block!';

function ScrollArea({
    className,
    children,
    viewportRef,
    viewportProps,
    ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
    viewportRef?: React.Ref<HTMLDivElement>;
    viewportProps?: React.ComponentProps<typeof ScrollAreaPrimitive.Viewport>;
}) {
    return (
        <ScrollAreaPrimitive.Root
            data-slot="scroll-area"
            className={cn('relative', className)}
            {...props}
        >
            {/* `viewportProps` is spread *before* `className` so a caller-supplied
                class merges with the base styling instead of replacing it. */}
            <ScrollAreaPrimitive.Viewport
                ref={viewportRef}
                data-slot="scroll-area-viewport"
                {...viewportProps}
                className={cn(
                    'size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-primary-200 focus-visible:outline-1',
                    viewportProps?.className,
                )}
            >
                {children}
            </ScrollAreaPrimitive.Viewport>
            <ScrollBar />
            <ScrollAreaPrimitive.Corner />
        </ScrollAreaPrimitive.Root>
    );
}

function ScrollBar({
    className,
    orientation = 'vertical',
    ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
    return (
        <ScrollAreaPrimitive.ScrollAreaScrollbar
            data-slot="scroll-area-scrollbar"
            data-orientation={orientation}
            orientation={orientation}
            className={cn(
                'flex touch-none p-px transition-colors select-none data-horizontal:h-2.5 data-horizontal:flex-col data-horizontal:border-t data-horizontal:border-t-transparent data-vertical:h-full data-vertical:w-2.5 data-vertical:border-l data-vertical:border-l-transparent',
                className,
            )}
            {...props}
        >
            <ScrollAreaPrimitive.ScrollAreaThumb
                data-slot="scroll-area-thumb"
                className="relative flex-1 rounded-full bg-neutral-300 dark:bg-neutral-600"
            />
        </ScrollAreaPrimitive.ScrollAreaScrollbar>
    );
}

export { ScrollArea, ScrollBar };
